import React, { useState } from 'react';
import { MCP_URL, MCP_PITCH, MCP_DOCS_PATH, MCP_TOOLS, MCP_ALLOWANCES, MCP_CLIENT_EXAMPLES, MCP_EXAMPLE_PROMPTS, mcpDesktopConfig } from '../../config/mcp';
import { copyText } from '../../utils/clipboard';

/**
 * Connect flow for the smart-list MCP server (`mcp.quantape.com/mcp`).
 *
 * Renders in two very different trees: inside the app's modal/toolbar (React
 * Query + Router available) AND inside the standalone `/mcp` route, which is
 * outside `QueryClientProvider` (same constraint as LegalPages). So: no
 * TanStack Query hooks, no react-router imports — links are plain `<a href>`.
 *
 * Deliberately provider-neutral copy throughout: MCP is an open protocol, so
 * nothing here singles out one AI assistant as "the" way to connect.
 */

// Compact monospace "code block" with its own Copy button — three of these
// appear per panel (endpoint / client config / one-liners), so this is a
// single implementation rather than three inline copies.
const CodeBlock = ({ value, what, variant, onCopy, multiline = false }) => {
  const [status, setStatus] = useState(null); // { ok: boolean } | null

  const handleCopy = async () => {
    const ok = await copyText(value);
    setStatus({ ok });
    onCopy?.(what);
    window.amplitude?.track?.('mcp_connect_copy', { what, variant });
    setTimeout(() => setStatus(null), 2000);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <pre
          style={{
            flex: 1,
            margin: 0,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: '12px',
            color: '#111827',
            background: '#f9fafb',
            border: '1px solid #f3f4f6',
            borderRadius: '6px',
            padding: '8px 10px',
            userSelect: 'all',
            whiteSpace: multiline ? 'pre-wrap' : 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {value}
        </pre>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            flexShrink: 0,
            fontSize: '12px',
            padding: '4px 10px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            background: 'white',
            color: '#374151',
            cursor: 'pointer',
          }}
        >
          Copy
        </button>
      </div>
      {status && (
        <p style={{ fontSize: '11px', color: status.ok ? '#16a34a' : '#dc2626', margin: '4px 0 0' }}>
          {status.ok ? 'Copied' : 'Copy failed — select the text and copy manually'}
        </p>
      )}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p style={{ fontSize: '12px', fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>{children}</p>
);

// `intro` defaults to the shared MCP_PITCH; a page that renders MCP_PITCH itself (as its own
// body-sized lead) passes intro={null} so the sentence appears once, not twice.
const McpConnectPanel = ({ variant = 'full', token, onCopy, intro = MCP_PITCH }) => {
  const clientConfig = mcpDesktopConfig(token);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>
      {intro && <p style={{ margin: 0 }}>{intro}</p>}

      <div>
        <SectionLabel>Ask things like</SectionLabel>
        <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {MCP_EXAMPLE_PROMPTS.map((prompt) => (
            <li key={prompt} style={{ fontStyle: 'italic', color: '#374151' }}>“{prompt}”</li>
          ))}
        </ul>
      </div>

      <div>
        <SectionLabel>MCP endpoint (Streamable HTTP)</SectionLabel>
        <CodeBlock value={MCP_URL} what="endpoint" variant={variant} onCopy={onCopy} />
      </div>

      <div>
        <SectionLabel>
          {token ? 'MCP client config — signed in (bigger allowance)' : 'MCP client config'}
        </SectionLabel>
        <CodeBlock value={clientConfig} what="client_config" variant={variant} onCopy={onCopy} multiline />
        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0', lineHeight: 1.45 }}>
          Paste into your client's MCP config (Cursor, VS Code, Claude Desktop, Windsurf, …).
        </p>
        {token && (
          <p style={{ fontSize: '11px', color: '#b45309', margin: '6px 0 0', lineHeight: 1.45 }}>
            This token is your full QuantApe session credential — treat it like a password; log out
            to revoke it.
          </p>
        )}
      </div>

      <details>
        <summary style={{ fontSize: '12px', fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
          Client examples
        </summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
          {MCP_CLIENT_EXAMPLES.map((c) => (
            <div key={c.client}>
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 4px' }}>
                <strong style={{ color: '#374151' }}>{c.client}</strong>
                {c.instructions ? `: ${c.instructions}` : ''}
              </p>
              {c.command && (
                <>
                  {c.commandLabel && (
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 4px' }}>{c.commandLabel}</p>
                  )}
                  <CodeBlock value={c.command} what={`cmd_${c.client}`} variant={variant} onCopy={onCopy} />
                </>
              )}
            </div>
          ))}
        </div>
      </details>

      {variant === 'full' && (
        <>
          <div>
            <SectionLabel>Tools</SectionLabel>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb', padding: '4px 8px' }}>Tool</th>
                    <th style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb', padding: '4px 8px' }}>Description</th>
                    <th style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb', padding: '4px 8px', whiteSpace: 'nowrap' }}>Free per day</th>
                    <th style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb', padding: '4px 8px', whiteSpace: 'nowrap' }}>Then per call</th>
                  </tr>
                </thead>
                <tbody>
                  {MCP_TOOLS.map((tool) => (
                    <tr key={tool.name}>
                      <td style={{ borderBottom: '1px solid #f3f4f6', padding: '4px 8px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', whiteSpace: 'nowrap' }}>
                        {tool.name}
                      </td>
                      <td style={{ borderBottom: '1px solid #f3f4f6', padding: '4px 8px' }}>{tool.description}</td>
                      <td style={{ borderBottom: '1px solid #f3f4f6', padding: '4px 8px', whiteSpace: 'nowrap' }}>
                        {tool.price == null
                          ? 'Unlimited'
                          : `${MCP_ALLOWANCES.anon} / ${MCP_ALLOWANCES.user} / ${MCP_ALLOWANCES.power.toLocaleString()}`}
                      </td>
                      <td style={{ borderBottom: '1px solid #f3f4f6', padding: '4px 8px', whiteSpace: 'nowrap' }}>
                        {tool.price == null ? '—' : `$${tool.price.toFixed(2)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, lineHeight: 1.45 }}>
            Free per day = anonymous (by IP) / signed in / power user. Beyond that, each call is paid in USDC via x402.
          </p>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, lineHeight: 1.45 }}>
            Informational only, not investment advice. Errors are never charged; no refunds for data quality.{' '}
            <a href="/terms#mcp" style={{ color: '#6b7280', textDecoration: 'underline' }}>Terms of Service</a>.
          </p>
        </>
      )}

      {variant === 'inline' && (
        <a href={MCP_DOCS_PATH} style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}>
          Full details →
        </a>
      )}
    </div>
  );
};

export default McpConnectPanel;
